import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import { Box, Card, CardContent, IconButton, Modal, Tooltip, Typography } from '@mui/material';
import { Notify } from 'notiflix';
import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import styled from 'styled-components';

/**
 * Muestra el QR generado por el back. El distribuidor puede:
 *  - Mostrarle la pantalla al jugador para que escanee.
 *  - Copiar el link para enviárselo por otro medio (whatsapp, etc).
 *  - Descargar el QR como PNG.
 */
const DeliveryQrModal = ({ open, payload, onClose }) => {
    if (!open || !payload) return null;

    const claimUrl = payload.claim_url;
    const moves = payload.moves;
    const expiresAt = payload.expires_at ? new Date(payload.expires_at).toLocaleString() : 'sin expiración';

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(claimUrl);
            Notify.success('Enlace copiado al portapapeles.');
        } catch {
            Notify.failure('No se pudo copiar el enlace.');
        }
    };

    const downloadQr = () => {
        const canvas = document.getElementById('delivery-qr-canvas');
        if (!canvas) return;
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-jugadas-${payload.uuid}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Center>
                <StyledCard>
                    <CardContent sx={{ p: 2.5 }}>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <QrCode2OutlinedIcon />
                                </Box>
                                <Box>
                                    <Typography className="title">QR generado</Typography>
                                    <Typography className="subtitle">
                                        Pídele al jugador que lo escanee, o envíale el enlace.
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        <QrBox>
                            <QRCodeCanvas
                                id="delivery-qr-canvas"
                                value={claimUrl ?? ''}
                                size={220}
                                level="M"
                                includeMargin
                            />
                        </QrBox>

                        <Stat>
                            <span>Jugadas a entregar</span>
                            <strong>{moves}</strong>
                        </Stat>

                        <Stat>
                            <span>Expira</span>
                            <strong>{expiresAt}</strong>
                        </Stat>

                        <LinkRow>
                            <Box className="link-text" title={claimUrl}>{claimUrl}</Box>
                            <Tooltip title="Copiar enlace">
                                <IconButton size="small" onClick={copyLink}>
                                    <ContentCopyOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </LinkRow>

                        <Actions>
                            <DownloadButton onClick={downloadQr}>
                                Descargar QR (PNG)
                            </DownloadButton>
                            <CloseButton onClick={onClose}>
                                Cerrar
                            </CloseButton>
                        </Actions>

                        <Hint>
                            Mientras este QR esté pendiente, las {moves} jugadas quedan reservadas
                            y no podrás entregarlas a otra persona. Si lo cancelas desde el historial,
                            las jugadas vuelven a tu cuenta.
                        </Hint>
                    </CardContent>
                </StyledCard>
            </Center>
        </Modal>
    );
};

export default DeliveryQrModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 460px;
    outline: none;
`;

const StyledCard = styled(Card)`
    border-radius: 18px !important;
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 18px 38px rgba(15, 34, 77, 0.18) !important;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;
    overflow: hidden;

    &::before {
        content: "";
        display: block;
        height: 5px;
        width: 100%;
        background: linear-gradient(90deg, #10285f 0%, #d6aa3a 100%);
    }
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 1rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        min-width: 0;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .title {
        color: #10285f;
        font-weight: 900;
        font-size: 1.05rem;
        line-height: 1.2;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.78rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const QrBox = styled(Box)`
    display: flex;
    justify-content: center;
    padding: 0.8rem;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(15, 34, 77, 0.1);
    margin-bottom: 0.9rem;
`;

const Stat = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.85rem;
    border-radius: 10px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    margin-bottom: 0.4rem;

    span {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.78rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    strong {
        color: #10285f;
        font-size: 0.85rem;
        font-weight: 900;
    }
`;

const LinkRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.6rem;
    border-radius: 10px;
    background: #fff;
    border: 1px dashed rgba(15, 34, 77, 0.18);
    margin-top: 0.6rem;

    .link-text {
        flex: 1;
        min-width: 0;
        font-family: 'Roboto Mono', monospace;
        font-size: 0.72rem;
        color: #10285f;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const Actions = styled(Box)`
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;

    @media (max-width: 480px) {
        flex-direction: column;
    }
`;

const baseButton = `
    flex: 1;
    border: none;
    border-radius: 12px;
    padding: 0.6rem 0.9rem;
    font-weight: 900;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    transition: 0.18s ease;
`;

const DownloadButton = styled.button`
    ${baseButton}
    color: #fff;
    background: linear-gradient(90deg, #10285f 0%, #1a2f5c 100%);
    box-shadow: 0 6px 14px rgba(15, 34, 77, 0.18);

    &:hover {
        background: linear-gradient(90deg, #1a2f5c 0%, #10285f 100%);
    }
`;

const CloseButton = styled.button`
    ${baseButton}
    color: #10285f;
    background: #fff;
    border: 1px solid rgba(214, 170, 58, 0.55) !important;

    &:hover {
        background: rgba(214, 170, 58, 0.12);
    }
`;

const Hint = styled(Typography)`
    margin-top: 0.9rem !important;
    color: rgba(15, 34, 77, 0.62) !important;
    font-size: 0.74rem !important;
    line-height: 1.35 !important;
    text-align: center;
`;
